import { FC, useEffect, useMemo } from 'react';

import { getSeasonConfig } from './get-season-config';
import { useSeasonalTheme } from './seasonal-theme-context';

/**
 * Seasonal Theme Overrides Component
 *
 * Applies seasonal theme styling by:
 * 1. Adding a theme-specific class to the root element
 * 2. Overriding Chakra UI tokens with season colors
 * 3. Inverting the color scheme for a dark mode
 * 4. Replacing "Give recognition" button text with seasonal alternatives via DOM manipulation
 */
export const SeasonalThemeOverrides: FC = () => {
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const seasonConfig = getSeasonConfig();

	// Pick a random button text on mount
	const buttonText = useMemo(() => {
		const randomIndex = Math.floor(Math.random() * seasonConfig.buttonTexts.length);
		return seasonConfig.buttonTexts[randomIndex];
	}, [seasonConfig.buttonTexts]);

	// Add/remove seasonal theme class on root element
	useEffect(() => {
		const root = document.documentElement;

		if (isSeasonalThemeEnabled) {
			root.classList.add(seasonConfig.themeClassName);
		} else {
			root.classList.remove(seasonConfig.themeClassName);
		}

		return () => {
			root.classList.remove(seasonConfig.themeClassName);
		};
	}, [isSeasonalThemeEnabled, seasonConfig.themeClassName]);

	// Replace "Give recognition" button text with seasonal text
	useEffect(() => {
		if (!isSeasonalThemeEnabled) {
			return;
		}

		let foundButton = false;

		const replaceButtonText = () => {
			// Find the give-box-actions container
			const giveBoxActions = document.querySelector('[data-testid="give-box-actions"]');
			if (!giveBoxActions) {
				return false;
			}

			// Find all buttons within give-box-actions
			const buttons = giveBoxActions.querySelectorAll('button');
			let replacedAny = false;

			buttons.forEach((button) => {
				// Look through all text nodes in the button and its children
				const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null);

				let node;
				while ((node = walker.nextNode())) {
					if (node.textContent && node.textContent.trim()) {
						const text = node.textContent.trim();
						// Only replace if it contains "Give recognition" or similar text
						if (text.includes('Give recognition') || text.includes('Give Recognition') || text === 'Give recognition') {
							node.textContent = node.textContent.replace(/Give recognition/gi, buttonText);
							replacedAny = true;
						}
					}
				}
			});

			return replacedAny;
		};

		// Try to replace immediately
		if (replaceButtonText()) {
			foundButton = true;
		}

		// If button not found, keep trying with an interval
		let intervalId: number | undefined;
		if (!foundButton) {
			intervalId = window.setInterval(() => {
				if (replaceButtonText()) {
					foundButton = true;
					if (intervalId) {
						clearInterval(intervalId);
					}
				}
			}, 200); // Check every 200ms

			// Stop trying after 10 seconds
			setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 10000);
		}

		// Watch for DOM changes (in case buttons are added dynamically or content changes)
		const observer = new MutationObserver(() => {
			replaceButtonText();
		});

		// Observe the entire body since give-box-actions might not exist yet
		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
			observer.disconnect();
		};
	}, [isSeasonalThemeEnabled, buttonText]);

	if (!isSeasonalThemeEnabled) {
		return null;
	}

	const { themeClassName, primaryColor } = seasonConfig;

	return (
		<style>
			{`
				/* Seasonal Dark Theme - Applied when .${themeClassName} class is on root */
				.${themeClassName} {
					/* Primary colors -> Seasonal Color */
					--chakra-colors-primary-fg: ${primaryColor.fg} !important;
					--chakra-colors-primary-fgStrong: ${primaryColor.fgStrong} !important;
					--chakra-colors-primary-fgStronger: ${primaryColor.fgStronger} !important;
					--chakra-colors-primary-fgStrongest: ${primaryColor.fgStrongest} !important;
					--chakra-colors-primary-bg: ${primaryColor.bg} !important;
					--chakra-colors-primary-bgHover: ${primaryColor.bgHover} !important;
					--chakra-colors-primary-bgActive: ${primaryColor.bgActive} !important;
					--chakra-colors-primary-bgAlpha: ${primaryColor.bgAlpha} !important;
					--chakra-colors-primary-bgAlphaHover: ${primaryColor.bgAlphaHover} !important;
					--chakra-colors-primary-bgAlphaActive: ${primaryColor.bgAlphaActive} !important;

					/* Dark backgrounds - Canvas is darkest, surfaces are lighter */
					--chakra-colors-bg-canvas: #0a0a0a !important;
					--chakra-colors-bg-surface: #1a1a1a !important;
					--chakra-colors-bg-soft: #2a2a2a !important;
					--chakra-colors-bg-subtle: #1f1f1f !important;

					/* Invert text colors for dark mode */
					--chakra-colors-text-default: #ffffff !important;
					--chakra-colors-text-soft: #d1d5db !important;
					--chakra-colors-text-softer: #9ca3af !important;
					--chakra-colors-text-softest: #6b7280 !important;

					/* Gray scale inverted */
					--chakra-colors-gray-25: #2a2a2a !important;
					--chakra-colors-gray-50: #2d3039 !important;
					--chakra-colors-gray-100: #3c3f49 !important;
					--chakra-colors-gray-200: #515661 !important;
					--chakra-colors-gray-300: #656c7b !important;
					--chakra-colors-gray-400: #898f9a !important;
					--chakra-colors-gray-500: #a6acb5 !important;
					--chakra-colors-gray-600: #c6cbd2 !important;
					--chakra-colors-gray-700: #dbdfe6 !important;
					--chakra-colors-gray-800: #edeef3 !important;
					--chakra-colors-gray-900: #f6f6f9 !important;

					/* Border colors for card separation */
					--chakra-colors-border-default: #3a3a3a !important;
					--chakra-colors-border-soft: #333333 !important;

					/* Override white to card background (lighter than canvas) */
					--chakra-colors-white: #242424 !important;
					
					/* Keep black for text */
					--chakra-colors-black: #ffffff !important;
				}

				/* Apply dark background to body */
				.${themeClassName} body {
					background-color: #0a0a0a !important;
					color: #ffffff !important;
				}

				/* Cards - Give them definition with borders and slightly lighter bg */
				.${themeClassName} [data-theme="light"],
				.${themeClassName} div[class*="Card"],
				.${themeClassName} div[class*="card"] {
					background-color: #242424 !important;
					border: 1px solid #3a3a3a !important;
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4) !important;
				}

				/* Bonus/Recognition cards - specific styling */
				.${themeClassName} div[data-component="bonus-card"],
				.${themeClassName} div[data-component="recognition-card"] {
					background-color: #242424 !important;
					border: 1px solid #3a3a3a !important;
				}

				/* Ensure proper contrast for nested elements */
				.${themeClassName} div[style*="background-color: white"],
				.${themeClassName} div[style*="backgroundColor: white"] {
					background-color: #242424 !important;
				}

				/* Modal and overlay backgrounds */
				.${themeClassName} [role="dialog"] {
					background-color: #1a1a1a !important;
				}

				/* Inputs and form elements */
				.${themeClassName} input,
				.${themeClassName} textarea,
				.${themeClassName} select {
					background-color: #1a1a1a !important;
					border-color: #3a3a3a !important;
					color: #ffffff !important;
				}

				.${themeClassName} input::placeholder,
				.${themeClassName} textarea::placeholder {
					color: #6b7280 !important;
				}

				/* Gratitude Gauge - Override the inline background colors */
				.${themeClassName} [data-testid*="dimension-box"] {
					background-color: #242424 !important;
					border: 1px solid #3a3a3a !important;
				}

				/* Gratitude Gauge container (the white popup box) */
				.${themeClassName} div[class*="SlideFade"] > div[style*="background-color: white"],
				.${themeClassName} div[style*="background-color: rgb(255, 255, 255)"] {
					background-color: #242424 !important;
					border: 1px solid #3a3a3a !important;
				}

				/* Target the specific light gray backgrounds (#F9FAFB) */
				.${themeClassName} div[style*="#F9FAFB"],
				.${themeClassName} div[style*="rgb(249, 250, 251)"] {
					background-color: #2a2a2a !important;
				}
			`}
		</style>
	);
};
